/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react';
import './GoogleSignInTest.css';
// axios not needed; using fetch for initiate

const GoogleSignInTest = () => {
  const outputRef = useRef(null);
  const errorContainerRef = useRef(null);
  const fallbackContainerRef = useRef(null);
  const stateInputRef = useRef(null);
  const nonceInputRef = useRef(null);
  const initNonceInputRef = useRef(null);

  const [googleToken, setGoogleToken] = useState('');
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [linkBearerToken, setLinkBearerToken] = useState('');

  // Configuration object
  const cfg = {
    google_client_id:
      '1001793325537-hrel63kpn41ildsfhtddntkkugsbrego.apps.googleusercontent.com',
    state: 'u-a0tI7B4G5BUk3i_4U2r4fDnHYGwoxy2N6KAnuDAp0',
    nonce: 'kvDMQ8bLyt81fMHMfbGqBO2ex3sBnCSry_twQRJqsYE',
  };

  const log = (msg) => {
    if (outputRef.current) {
      outputRef.current.textContent += `${msg}\n`;
    }
  };

  const showError = (msg) => {
    if (errorContainerRef.current) {
      errorContainerRef.current.textContent = `Error: ${msg}`;
      errorContainerRef.current.style.display = 'block';
    }
    if (fallbackContainerRef.current) {
      fallbackContainerRef.current.style.display = 'block';
    }
  };

  const handleResp = async (resp) => {
    const idToken = resp.credential; // <-- JWT
    setGoogleToken(idToken); // Store the token for manual testing
    log(`ID TOKEN:\n${idToken}`);

    // Log additional payload information if available
    if (resp.state) {
      log(`STATE: ${resp.state}`);
    }
    if (resp.nonce) {
      log(`NONCE: ${resp.nonce}`);
    }

    // === WHAT HAPPENS NEXT ===
    log('\n=== PROCESSING THE TOKEN ===');

    try {
      // 1. Decode the JWT token to get user information
      const tokenParts = idToken.split('.');
      let payload;
      if (tokenParts.length === 3) {
        payload = JSON.parse(atob(tokenParts[1]));
        log('DECODED USER INFO:');
        log(`Email: ${payload.email}`);
        log(`Name: ${payload.name}`);
        log(`User ID: ${payload.sub}`);
        log(`Picture: ${payload.picture}`);
      }

      // 2. Validate the token (in production, verify with Google's public keys)
      log('\n=== TOKEN VALIDATION ===');
      log(`Token issuer: ${payload?.iss || 'Unknown'}`);
      log(`Token audience: ${payload?.aud || 'Unknown'}`);
      log(
        `Token expiration: ${new Date(
          (payload?.exp || 0) * 1000
        ).toLocaleString()}`
      );

      // 3. Check if token is expired
      const currentTime = Math.floor(Date.now() / 1000);
      if (payload?.exp && payload.exp < currentTime) {
        log('ERROR: Token has expired!');
        return;
      }

      // 4. Verify audience matches your client ID
      if (payload?.aud !== cfg.google_client_id) {
        log('ERROR: Token audience doesn&apos;t match your client ID!');
        return;
      }

      log('✅ Token validation passed!');

      // 5. Now you can make API calls to your backend
      log('\n=== READY FOR BACKEND API CALL ===');
      log('You can now send this token to your backend API');
      log('Example API call structure:');
      log(`
POST /auth/google/link
Headers: {
  "Content-Type": "application/json",
  "Authorization": "Bearer YOUR_OTP_ACCESS_TOKEN"
}
Body: {
  "id_token": "${idToken}",
  "state": "${cfg.state}"
}
      `);

      // 6. Optional: Auto-call your backend API (uncomment to enable)
      // await callBackendAPI(idToken, cfg.state);
    } catch (error) {
      log(`ERROR processing token: ${error.message}`);
    }
  };

  // Manual testing function
  const testHandleRespManually = () => {
    if (!googleToken) {
      alert('Please sign in with Google first to get a token!');
      return;
    }

    const stateValue = stateInputRef.current?.value.trim() || cfg.state;
    const nonceValue = nonceInputRef.current?.value.trim() || cfg.nonce;

    // Create the payload object similar to what Google would send
    const manualPayload = {
      credential: googleToken,
      state: stateValue,
      nonce: nonceValue,
    };

    log('\n=== MANUAL TEST TRIGGER ===');
    log('Testing handleResp with custom state/nonce:');
    log(`State: ${stateValue}`);
    log(`Nonce: ${nonceValue}`);
    log('=== CALLING handleResp ===');

    // Call the same handleResp function
    handleResp(manualPayload);
  };

  const clearOutput = () => {
    if (outputRef.current) outputRef.current.textContent = '';
    setGoogleToken('');
  };

  const retryGoogleSignIn = () => {
    // Clear previous errors
    if (errorContainerRef.current)
      errorContainerRef.current.style.display = 'none';
    if (fallbackContainerRef.current)
      fallbackContainerRef.current.style.display = 'none';

    // Clear output
    clearOutput();

    // Retry initialization
    initializeGoogleSignIn();
  };

  const initializeGoogleSignIn = () => {
    try {
      if (typeof google === 'undefined' || !google.accounts) {
        showError(
          'Google Identity Services library not loaded. Please check your internet connection.'
        );
        return;
      }

      // Get the nonce value from the input field or use default
      const nonceValue = initNonceInputRef.current?.value.trim() || cfg.nonce;

      google.accounts.id.initialize({
        client_id: cfg.google_client_id,
        nonce: nonceValue, // Use user-provided nonce or default
        callback: handleResp,
        auto_select: false,
      });

      setIsInitialized(true);
      log('✅ Google Sign-In initialized successfully!');
      log(`Using nonce: ${nonceValue}`);
      log(
        'Click the "Sign In with Google" button to start the authentication process.'
      );

      // Add error handling for the prompt
      google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          const reason = notification.getNotDisplayedReason();
          const skippedReason = notification.getSkippedReason();

          if (reason === 'fedcm_disabled' || reason === 'fedcm_blocked') {
            showError(
              'FedCM (Federated Credential Management) is disabled. Please enable it in your browser settings.'
            );
          } else if (reason === 'opt_out_or_no_session') {
            showError(
              'User is not signed in to Google or has opted out of automatic sign-in.'
            );
          } else if (reason === 'secure_http_required') {
            showError('HTTPS is required for Google Sign-In.');
          } else if (reason === 'suppressed_by_user') {
            showError('Sign-in was suppressed by the user.');
          } else if (reason === 'unregistered_origin') {
            showError('This origin is not registered for Google Sign-In.');
          } else if (reason === 'unknown_reason') {
            showError('Unknown reason for not displaying the sign-in prompt.');
          } else if (skippedReason === 'auto_cancel') {
            showError('Sign-in was automatically cancelled.');
          } else if (skippedReason === 'user_cancel') {
            showError('User cancelled the sign-in process.');
          } else if (skippedReason === 'tap_outside') {
            showError('User tapped outside the sign-in dialog.');
          } else {
            showError(`Sign-in not displayed: ${reason || skippedReason}`);
          }
        }
      });
    } catch (error) {
      showError(`Failed to initialize Google Sign-In: ${error.message}`);
    }
  };

  const handleGoogleSignInClick = () => {
    if (!isGoogleLoaded) {
      showError(
        'Google Identity Services library not loaded yet. Please wait...'
      );
      return;
    }

    // Get the nonce value for initialization
    const nonceValue = initNonceInputRef.current?.value.trim() || cfg.nonce;

    // Re-initialize if nonce has changed or not initialized yet
    if (
      !isInitialized ||
      (initNonceInputRef.current?.value.trim() &&
        initNonceInputRef.current.value.trim() !== cfg.nonce)
    ) {
      initializeGoogleSignIn();
    }

    // Trigger the Google Sign-In prompt
    if (
      typeof google !== 'undefined' &&
      google.accounts &&
      google.accounts.id
    ) {
      google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          const reason = notification.getNotDisplayedReason();
          const skippedReason = notification.getSkippedReason();

          if (reason === 'fedcm_disabled' || reason === 'fedcm_blocked') {
            showError(
              'FedCM (Federated Credential Management) is disabled. Please enable it in your browser settings.'
            );
          } else if (reason === 'opt_out_or_no_session') {
            showError(
              'User is not signed in to Google or has opted out of automatic sign-in.'
            );
          } else if (reason === 'secure_http_required') {
            showError('HTTPS is required for Google Sign-In.');
          } else if (reason === 'suppressed_by_user') {
            showError('Sign-in was suppressed by the user.');
          } else if (reason === 'unregistered_origin') {
            showError('This origin is not registered for Google Sign-In.');
          } else if (reason === 'unknown_reason') {
            showError('Unknown reason for not displaying the sign-in prompt.');
          } else if (skippedReason === 'auto_cancel') {
            showError('Sign-in was automatically cancelled.');
          } else if (skippedReason === 'user_cancel') {
            showError('User cancelled the sign-in process.');
          } else if (skippedReason === 'tap_outside') {
            showError('User tapped outside the sign-in dialog.');
          } else {
            showError(`Sign-in not displayed: ${reason || skippedReason}`);
          }
        }
      });
    }
  };

  // Initiate secondary Google linking via JSON, then navigate (avoid XHR redirect to Google)
  const handleGoogleSignIn = async () => {
    try {
      // Read Bearer token from the input and normalize
      let token = (linkBearerToken || '').trim();
      if (!token) {
        showError('Please paste the Bearer token before initiating the link.');
        return;
      }
      if (token.toLowerCase().startsWith('bearer ')) {
        token = token.slice(7).trim();
      }

      const res = await fetch(
        'https://api-stage.ahedge.link/ncandidates/auth/secondary/google/initiate',
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'include',
        }
      );

      if (res.status === 409) {
        const data = await res.json().catch(() => ({}));
        const method = data?.method || 'this method';
        alert(`Already configured: ${method}`);
        return;
      }

      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        const authUrl = data?.auth_url || data?.data?.auth_url;
        if (authUrl) {
          window.location.assign(authUrl);
          return;
        }
      }

      console.error('Failed to initiate Google linking:', res.status);
      alert('Failed to start Google sign-in');
    } catch (error) {
      if (error?.response?.status === 409) {
        const method = error?.response?.data?.method || 'this method';
        alert(`Already configured: ${method}`);
        return;
      }
      console.error('Failed to initiate Google linking:', error);
      alert('Failed to start Google sign-in');
    }
  };

  useEffect(() => {
    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    script.onload = () => {
      setIsGoogleLoaded(true);
      log('✅ Google Identity Services library loaded successfully!');
      log(
        'Click the "Sign In with Google" button to start the authentication process.'
      );
    };

    script.onerror = () => {
      showError('Failed to load Google Identity Services script');
    };

    return () => {
      // Cleanup script if component unmounts
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="google-signin-test">
      <div className="header-section">
        <h2>Google Sign-In Test Page</h2>
        <p>This page tests Google Sign-In integration with manual trigger.</p>

        {/* Google Sign-In Button */}
        <div className="google-signin-button-container">
          <div className="nonce-input-container">
            <label htmlFor="init-nonce-input">
              Nonce for Google Sign-In Initialization:
            </label>
            <input
              type="text"
              id="init-nonce-input"
              ref={initNonceInputRef}
              placeholder={cfg.nonce}
              className="nonce-input"
            />
            <small className="nonce-help">
              Leave empty to use default nonce value
            </small>
          </div>

          <button
            type="button"
            onClick={handleGoogleSignInClick}
            className="google-signin-button"
            disabled={!isGoogleLoaded}
          >
            {!isGoogleLoaded
              ? 'Loading Google Services...'
              : 'Sign In with Google'}
          </button>
        </div>

        {/* Secondary Google Link (Redirect) Test Button */}
        <div
          className="google-link-initiate-container"
          style={{ marginTop: '12px' }}
        >
          <div className="input-group" style={{ marginBottom: '8px' }}>
            <label htmlFor="link-bearer-token">Bearer Token for Linking:</label>
            <textarea
              id="link-bearer-token"
              className="text-input"
              rows={3}
              placeholder="Paste Bearer token here (with or without 'Bearer ' prefix)"
              value={linkBearerToken}
              onChange={(e) => setLinkBearerToken(e.target.value)}
            />
            <small>
              This token will be sent as Authorization: Bearer &lt;token&gt;
            </small>
          </div>
          <button
            type="button"
            onClick={handleGoogleSignIn}
            className="primary-button"
          >
            Link Google Account
          </button>
        </div>

        {/* Current Payloads Information */}
        <div className="payloads-info">
          <h3>Current Payloads Being Passed:</h3>
          <div className="payload-section">
            <h4>1. Google Identity Services Configuration:</h4>
            <pre>
              {`{
  "client_id": "${cfg.google_client_id}",
  "nonce": "USER_PROVIDED_OR_DEFAULT",  // From input field or config default
  "callback": "handleResp",
  "auto_select": false
}`}
            </pre>
          </div>

          <div className="payload-section">
            <h4>2. Expected Response Payload from Google:</h4>
            <pre>
              {`{
  "credential": "JWT_TOKEN_FROM_GOOGLE",  // The actual JWT token
  "state": "${cfg.state}",               // CSRF protection
  "nonce": "USER_PROVIDED_OR_DEFAULT"    // Replay attack protection
}`}
            </pre>
          </div>
        </div>
      </div>

      <div id="out" ref={outputRef} className="output-container" />

      <div
        id="error-container"
        ref={errorContainerRef}
        className="error-container"
      />

      <div
        id="fallback-container"
        ref={fallbackContainerRef}
        className="fallback-container"
      >
        <h3>FedCM is disabled. Try these solutions:</h3>
        <ol>
          <li>
            Click the lock/shield icon to the left of the URL bar and enable
            &quot;Federated Credential Management&quot;
          </li>
          <li>
            Go to <code>chrome://settings/content/federatedIdentity</code> and
            enable &quot;Allow sites to use federated identity API&quot;
          </li>
          <li>Clear site data and refresh the page</li>
          <li>Try in an incognito/private window</li>
        </ol>
        <button type="button" onClick={retryGoogleSignIn}>
          Retry Google Sign-In
        </button>
      </div>

      {/* Manual Testing Section */}
      <div className="manual-test-container">
        <h3>Manual Testing</h3>
        <p>
          After signing in with Google, you can test the handleResp function
          with custom state and nonce values:
        </p>

        <div className="token-display">
          <h4>Google Token Status:</h4>
          {googleToken ? (
            <div className="token-info">
              <span className="token-status success">✅ Token Available</span>
              <button
                type="button"
                onClick={clearOutput}
                className="clear-button"
              >
                Clear Token & Output
              </button>
            </div>
          ) : (
            <span className="token-status waiting">
              ⏳ Waiting for Google Sign-In...
            </span>
          )}
        </div>

        <div className="input-group">
          <label htmlFor="state-input">
            State (Optional - defaults to config value):
          </label>
          <input
            type="text"
            id="state-input"
            ref={stateInputRef}
            placeholder={cfg.state}
            className="text-input"
          />
        </div>

        <div className="input-group">
          <label htmlFor="nonce-input">
            Nonce (Optional - defaults to config value):
          </label>
          <input
            type="text"
            id="nonce-input"
            ref={nonceInputRef}
            placeholder={cfg.nonce}
            className="text-input"
          />
        </div>

        <div className="button-group">
          <button
            type="button"
            onClick={testHandleRespManually}
            className="primary-button"
            disabled={!googleToken}
          >
            Test handleResp with Custom Values
          </button>
        </div>

        <div className="payload-info">
          <h4>Payload Structure:</h4>
          <pre>
            {`{
  "credential": "JWT_TOKEN_FROM_GOOGLE",  // From Google Sign-In
  "state": "CUSTOM_STATE_VALUE",         // From input or config default
  "nonce": "CUSTOM_NONCE_VALUE"          // From input or config default
}`}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default GoogleSignInTest;
